import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { AdminTenantSummary } from '../api/client';
import { formatDate } from '../lib/format';
import { TenantStatusBadge } from './StatusBadge';

export interface TenantTableProps {
  tenants: AdminTenantSummary[];
  /** Whether the search/filter inputs are shown. Default true. */
  showFilters?: boolean;
}

/**
 * Client-side filterable tenant table. The list endpoint already takes
 * `?search` and `?suspended` on the server, but this component performs
 * the same filtering locally over the already-fetched page so the UI
 * stays responsive while typing.
 *
 * Tested in TenantTable.test.tsx: search-by-slug, status filter, empty
 * state.
 */
export function TenantTable({ tenants, showFilters = true }: TenantTableProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'suspended'>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants.filter((t) => {
      if (filter === 'active' && t.status !== 'active') return false;
      if (filter === 'suspended' && t.status !== 'suspended') return false;
      if (!q) return true;
      return (
        t.slug.toLowerCase().includes(q) ||
        (t.name?.toLowerCase().includes(q) ?? false) ||
        (t.custom_domain?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [tenants, search, filter]);

  return (
    <div className="space-y-3">
      {showFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input max-w-xs"
            placeholder="Search by name, slug or domain"
            value={search}
            data-testid="tenant-search"
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="input max-w-[10rem]"
            value={filter}
            data-testid="tenant-status-filter"
            onChange={(e) =>
              setFilter(e.target.value as 'all' | 'active' | 'suspended')
            }
          >
            <option value="all">All</option>
            <option value="active">Active only</option>
            <option value="suspended">Suspended only</option>
          </select>
          <span className="text-xs text-slate-500" data-testid="tenant-count">
            {filtered.length} of {tenants.length}
          </span>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1">Name</th>
              <th className="px-2 py-1">Slug</th>
              <th className="px-2 py-1">Status</th>
              <th className="px-2 py-1">Quota/day</th>
              <th className="px-2 py-1">Custom domain</th>
              <th className="px-2 py-1">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-2 py-4 text-center text-slate-500">
                  No tenants match.
                </td>
              </tr>
            ) : (
              filtered.map((t) => (
                <tr
                  key={t.id}
                  data-testid={`tenant-row-${t.slug}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-2 py-1">
                    <Link className="text-brand hover:underline" to={`/tenants/${t.id}`}>
                      {t.name ?? '(unnamed)'}
                    </Link>
                    {t.is_sandbox ? (
                      <span className="ml-2 text-xs text-slate-500">(sandbox)</span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1 font-mono text-xs">{t.slug}</td>
                  <td className="px-2 py-1">
                    <TenantStatusBadge status={t.status} />
                  </td>
                  <td className="px-2 py-1">{t.quota_contracts_per_day}</td>
                  <td className="px-2 py-1 text-slate-600">
                    {t.custom_domain ? `${t.custom_domain} (${t.custom_domain_status})` : '—'}
                  </td>
                  <td className="px-2 py-1 text-slate-600">{formatDate(t.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
